import { Reveal } from "../Reveal";
import { MapPin } from "lucide-react";

const LOCATIONS = [
  {
    id: "paddington",
    name: "Paddington",
    suburb: "Paddington, NSW 2021",
    address: "64 William Street",
    note: "Inner-city studio — black & grey, guest residencies",
  },
  {
    id: "bondi",
    name: "Bondi Beach",
    suburb: "Bondi Beach, NSW 2026",
    address: "177 Glenayr Avenue",
    note: "Coastal flagship — fine line & micro-realism",
  },
];

function mapEmbed(loc) {
  const q = encodeURIComponent(`${loc.address}, ${loc.suburb}, Australia`);
  return `https://www.google.com/maps?q=${q}&output=embed`;
}

export default function Location() {
  return (
    <section
      id="location"
      data-testid="location-section"
      className="bg-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-16 md:mb-24 text-center">
          <Reveal delay={0.1}>
            <h2
              className="font-display text-[#0A0A0A] uppercase mx-auto"
              style={{
                fontFamily: "'Jost', 'Futura', 'Helvetica Neue', sans-serif",
                fontSize: "clamp(1.5rem, 4.6vw, 4rem)",
                letterSpacing: "0.22em",
                paddingLeft: "0.22em",
                fontWeight: 200,
                lineHeight: 1.1,
              }}
            >
              LOCATIONS&nbsp;&amp;&nbsp;CONTACT&nbsp;INFO
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
              <a href="tel:+61415556026" data-testid="contact-phone" className="hover:opacity-60 text-[#0A0A0A]">
                +61 415 556 026
              </a>
              <span className="text-[#CCC]">·</span>
              <a href="mailto:jayinkacademy@gmail.com" data-testid="contact-email" className="hover:opacity-60 text-[#0A0A0A]">
                jayinkacademy@gmail.com
              </a>
              <span className="text-[#CCC]">·</span>
              <a
                href="https://instagram.com/jayink__academy"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="hover:opacity-60 text-[#0A0A0A]"
              >
                @jayink__academy
              </a>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {LOCATIONS.map((loc, idx) => (
            <Reveal key={loc.id} delay={0.1 * idx}>
              <article data-testid={`location-card-${loc.id}`} className="group">
                <div className="aspect-[4/3] w-full bg-white overflow-hidden border border-[#E5E5E5]">
                  {loc.openingSoon ? (
                    <div
                      data-testid={`location-opening-soon-${loc.id}`}
                      className="relative w-full h-full flex items-center justify-center overflow-hidden bg-white"
                    >
                      <img
                        src="/jay-ink-logo.png"
                        alt=""
                        aria-hidden
                        draggable={false}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none select-none opacity-35"
                        style={{ width: "140%", height: "auto" }}
                      />
                      <p className="relative z-10 text-[11px] md:text-xs tracking-[0.45em] uppercase text-[#0A0A0A]/70" style={{ paddingLeft: "0.45em" }}>
                        Opening Soon
                      </p>
                    </div>
                  ) : (
                    <iframe
                      title={`${loc.name} map`}
                      src={mapEmbed(loc)}
                      width="100%"
                      height="100%"
                      style={{ border: 0, filter: "grayscale(100%) contrast(1.05)" }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      data-testid={`location-map-${loc.id}`}
                      allowFullScreen
                    />
                  )}
                </div>
                <div className="pt-6 flex items-start justify-between gap-6">
                  <div>
                    <div className="overline mb-3 flex items-center gap-2">
                      <MapPin size={11} /> {loc.id === "paddington" ? "Studio 01" : "Studio 02"}
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-2">
                      {loc.name}
                      {loc.badge && (
                        <span
                          data-testid={`location-badge-${loc.id}`}
                          className="ml-3 align-middle text-[10px] tracking-[0.3em] uppercase text-[#666] font-sans font-light"
                        >
                          ( {loc.badge} )
                        </span>
                      )}
                    </h3>
                    <p className="text-base font-light text-[#0A0A0A]">
                      {loc.address}
                      <br />
                      <span className="text-[#666]">{loc.suburb}</span>
                    </p>
                    <p className="mt-3 text-sm text-[#666]">{loc.note}</p>
                  </div>
                  {!loc.openingSoon && (
                    <a
                      href={`https://www.google.com/maps?q=${encodeURIComponent(loc.address + ", " + loc.suburb)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`location-directions-${loc.id}`}
                      className="shrink-0 text-[10px] tracking-[0.25em] uppercase border-b border-[#0A0A0A] pb-1 hover:opacity-60"
                    >
                      Directions →
                    </a>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
